import React, { useState, useEffect } from "react";
import DateInput from "../../../common/components/Input";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { newDentistSchema } from "../schema/newDentistSchema";
import { StyledDialog } from "../../../common/components/Dialog";
import { Stack } from "@mui/system";
import { StyledButton } from "../../../common/components/Button";
import { createDentist } from "../../../api/dentistApi";
import { getAllDentalClinics } from "../../../api/dentistApi";
import { StyledTextField } from "../../../common/components/TextField/styles";
import { Controller } from "react-hook-form";
import { Autocomplete } from "@mui/material";
import { TextField } from "@mui/material";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Box,
} from "@mui/material";
import { red } from "@mui/material/colors";

const NewDentistDialog = ({ open, onClose, onNewDentist }) => {
  const [loading, setLoading] = useState(false);
  const [dentalClinics, setDentalClinics] = useState([
    {
      id: "c50a281b-c018-4f98-b566-a39f97331ac1",
      name: "Specify your dental clinic",
    },
  ]);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    reset,
    watch,
    setError,
  } = useForm({
    resolver: yupResolver(newDentistSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      dentalClinicId: "",
      dentalClinicName: "",
      dateOfBirth: null,
    },
  });
  const dentalClinicIdData = watch("dentalClinicId");
  useEffect(() => {
    const getDentalClinics = async () => {
      const response = await getAllDentalClinics();

      setDentalClinics((prev) => [...prev, ...response.data]);
    };
    getDentalClinics();
  }, []);

  const handleNewDentistSubmit = async (values) => {
    if (loading) return;
    try {
      setLoading(true);

      const { data } = await createDentist({
        ...values,
        dateOfBirth: new Date(values.dateOfBirth).toISOString(),
      });
      onNewDentist(data);

      onClose();
      setLoading(false);
      reset();
    } catch (err) {
      setLoading(false);
      setError("submission", {
        type: "custom",
        message: err.response.data.message,
      });
      setError("email", {
        type: "in use",
        message: "Invalid email.",
      });
      console.log(err.response.data);
    }
  };

  const handleClose = () => {
    if (loading) {
      return;
    }
    onClose();
    reset();
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      {loading && <LinearProgress />}
      <form onSubmit={handleSubmit(handleNewDentistSubmit)}>
        <DialogTitle>New dentist</DialogTitle>
        <DialogContent>
          <Box sx={{ width: "100%" }}>
            <Stack sx={{ paddingY: 1 }}>
              <StyledTextField
                label="First name"
                id="firstName"
                variant="outlined"
                error={errors.firstName}
                helperText={errors.firstName?.message}
                disabled={loading}
                {...register("firstName")}
              />
              <StyledTextField
                label="Last name"
                id="lastName"
                variant="outlined"
                error={errors.lastName}
                helperText={errors.lastName?.message}
                disabled={loading}
                {...register("lastName")}
              />
              <StyledTextField
                label="Email address"
                id="email"
                type="email"
                variant="outlined"
                error={errors.email}
                helperText={errors.email?.message}
                disabled={loading}
                {...register("email")}
              />
              <Controller
                control={control}
                name="dentalClinicId"
                render={({ field: { onChange }, fieldState: { error } }) => (
                  <Autocomplete
                    options={dentalClinics}
                    onChange={(_, val) => {
                      onChange(val?.id || "");
                    }}
                    getOptionLabel={(option) => option.name || option}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={Boolean(error)}
                        FormHelperTextProps={{ variant: "standard" }}
                        helperText={error && error.message}
                        placeholder="Dental Clinic"
                      />
                    )}
                    renderOption={(props, option) => {
                      return (
                        <li {...props} key={option.id}>
                          {option.name}
                        </li>
                      );
                    }}
                    sx={{ width: "100%", paddingBottom: "20px" }}
                  />
                )}
              />

              {dentalClinicIdData === "c50a281b-c018-4f98-b566-a39f97331ac1" ? (
                <StyledTextField
                  label="Dental clinic name"
                  id="dentalClinicName"
                  variant="outlined"
                  error={errors.dentalClinicName}
                  helperText={errors.dentalClinicName?.message}
                  disabled={loading}
                  {...register("dentalClinicName")}
                />
              ) : null}

              <DateInput
                label="Date of birth"
                name="dateOfBirth"
                control={control}
                disabled={loading}
              />
            </Stack>
          </Box>
          <Box sx={{ fontSize: "0.75rem", color: red[600] }}>
            {errors.submission && errors.submission.message}
          </Box>
        </DialogContent>
        <DialogActions>
          <StyledButton theme="green" type="submit" disabled={isSubmitting}>
            Submit
          </StyledButton>
        </DialogActions>
      </form>
    </StyledDialog>
  );
};

export default NewDentistDialog;
